<template>
  <base-drawer :show="show" title="Dashboard settings" @close="onClose">
    <drawer-section collapsible :expanded.sync="open.name" title="Name and description" icon="icon-pencil">
      <base-input required label="Name" :error="errs.name" v-model="draft.name" placeholder="Name"></base-input>
      <base-textarea label="Description" v-model="draft.description" :rows="4" placeholder="Description" />
    </drawer-section>

    <drawer-section collapsible :expanded.sync="open.filters" title="Global filter bar" icon="icon-bullet-list-67">
      <div class="d-flex align-items-center" style="gap: 8px">
        <el-switch active-color="#e14eca" v-model="draft.filterBar.enabled" />
        <label class="text-white mb-0">Show a global filter bar on this dashboard</label>
        <el-popover trigger="hover" placement="top" width="320" popper-class="otc-pop">
          <span>Viewers can change these filters for their own session and reset to your defaults. The bar overrides each widget's own settings only for the filters that have a value.</span>
          <i slot="reference" class="el-icon-info text-info" style="cursor: pointer"></i>
        </el-popover>
      </div>

      <template v-if="draft.filterBar.enabled">
        <p class="text-white mt-3 mb-2">Filters to show</p>
        <div class="widget-type-grid widget-type-grid--compact">
          <div v-for="k in FILTER_KEYS" :key="k" class="widget-type-card"
               :class="{ 'widget-type-card--active': draft.filterBar.keys.includes(k) }"
               @click="toggleKey(k)" @mouseenter="describedKey = k" @mouseleave="describedKey = null">
            <i class="tim-icons" :class="FILTER_ICONS[k]"></i>
            <span>{{ FILTER_LABELS[k] }}</span>
          </div>
        </div>
        <small class="text-white d-block" style="opacity: 0.7; min-height: 1.2em; margin-top: -0.75rem"><template v-if="describedKey"><i class="el-icon-info text-info mr-1"></i>{{ FILTER_DESCRIPTIONS[describedKey] }}</template></small>

        <p class="text-white mt-3 mb-2">Default values</p>
        <info-hint variant="neutral" icon="icon-bulb-63" style="width: 100%">
          Set the values viewers see when they open the dashboard. Press Apply in the bar to stage them, then Save.
        </info-hint>
        <div v-if="orderedKeys.length === 0" class="text-muted mt-2">Pick at least one filter above.</div>
        <div v-else class="mb-n3">
          <filter-bar :key="'cfg-' + orderedKeys.join('|')"
                      :filters="schema"
                      :initial-value="draft.filterBar.defaults"
                      @change="onDefaultsChange" />
        </div>
      </template>
    </drawer-section>

    <drawer-section collapsible :expanded.sync="open.access" title="Access and permissions" icon="icon-lock-circle">
      <div class="d-flex align-items-center" style="gap: 8px">
        <el-switch active-color="#e14eca" v-model="draft.accessible_by_all_users" />
        <label class="text-white mb-0">Accessible to everyone</label>
        <el-popover trigger="hover" placement="top" width="300" popper-class="otc-pop">
          <span>When enabled, everyone with global permissions can access this dashboard. Otherwise it is restricted to the roles and users you select. A selected role covers everyone who has it, including people given the role later.</span>
          <i slot="reference" class="el-icon-info text-info" style="cursor: pointer"></i>
        </el-popover>
      </div>
      <template v-if="!draft.accessible_by_all_users">
        <base-input label="Viewers" class="mt-3">
          <el-select multiple filterable v-model="draft.viewerKeys" class="select-primary" placeholder="Roles and users">
            <el-option-group v-if="roleSelectOptions.length > 0" label="Roles">
              <el-option v-for="r in roleSelectOptions" class="select-primary" :label="r.chipLabel" :value="r.key" :key="'VR' + r.key">{{ r.name }}</el-option>
            </el-option-group>
            <el-option-group label="Users">
              <el-option v-for="u in userOptions" class="select-primary" :label="userLabel(u)" :value="userKey(u.id)" :key="'VU' + u.id"></el-option>
            </el-option-group>
          </el-select>
        </base-input>
        <base-input label="Editors">
          <el-select multiple filterable v-model="draft.editorKeys" class="select-primary" placeholder="Roles and users">
            <el-option-group v-if="roleSelectOptions.length > 0" label="Roles">
              <el-option v-for="r in roleSelectOptions" class="select-primary" :label="r.chipLabel" :value="r.key" :key="'ER' + r.key">{{ r.name }}</el-option>
            </el-option-group>
            <el-option-group label="Users">
              <el-option v-for="u in userOptions" class="select-primary" :label="userLabel(u)" :value="userKey(u.id)" :key="'EU' + u.id"></el-option>
            </el-option-group>
          </el-select>
        </base-input>
      </template>
    </drawer-section>

    <drawer-section collapsible :expanded.sync="open.refresh" title="Automatic refresh" icon="icon-refresh-02">
      <div class="d-flex align-items-center" style="gap: 8px">
        <el-switch active-color="#e14eca" v-model="draft.refresh_enabled" />
        <label class="text-white mb-0">Refresh widgets automatically</label>
        <el-popover trigger="hover" placement="top" width="280" popper-class="otc-pop">
          <span>Every widget on this dashboard reloads its data on the interval below while the dashboard is open.</span>
          <i slot="reference" class="el-icon-info text-info" style="cursor: pointer"></i>
        </el-popover>
      </div>
      <div v-if="draft.refresh_enabled" class="mt-3">
        <label class="text-white d-block mb-2">Refresh interval</label>
        <segmented-toggle variant="pills" size="sm" v-model="draft.refresh_interval_minutes" :options="REFRESH_INTERVAL_TOGGLE_OPTIONS" />
      </div>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="loading" @click="onSave">
          {{ loading ? 'Saving...' : 'Save' }}
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import {
  FILTER_KEYS, FILTER_LABELS, FILTER_DESCRIPTIONS, FILTER_ICONS, buildFilterSchema, emptyConfig,
  REFRESH_INTERVAL_OPTIONS, snapRefreshInterval,
} from './dashboardGlobalFilters'
import type { FilterBarConfig, FilterKey, FilterOptions } from './dashboardGlobalFilters'
import { roleKey, userKey, userIdsFromKeys, mergeRolePermissions } from './dashboardAccess'
import type { RoleOption, AllowedRole } from './dashboardAccess'

interface User { id: number; first_name?: string; last_name?: string; email?: string }
interface AllowedUser { id: number; can_read: boolean; can_write: boolean }

interface DashboardSettingsPayload {
  name: string
  description: string
  refresh_enabled: boolean
  refresh_interval_minutes: number
  accessible_by_all_users: boolean
  allowed_users: AllowedUser[]
  allowed_roles: AllowedRole[]
  filter_bar: FilterBarConfig
}

const props = defineProps<{
  show: boolean
  dashboard: { name?: string; description?: string; refresh_enabled?: any; refresh_interval_minutes?: any; accessible_by_all_users?: any } | null
  users: User[]
  viewers: User[]
  editors: User[]
  roleOptions?: RoleOption[]
  allowedRoles?: AllowedRole[]
  filterConfig: FilterBarConfig
  filterOptions: FilterOptions | null
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', payload: DashboardSettingsPayload): void
}>()

// One entry per user id: the group's user list can repeat a user, and a selected viewer or
// editor may no longer be in that list (moved group), so they are merged in to keep a label.
function uniqueById(lists: User[][]): User[] {
  const seen = new Map<number, User>()
  for (const list of lists) for (const u of list || []) if (!seen.has(u.id)) seen.set(u.id, u)
  return Array.from(seen.values())
}

const userOptions = computed<User[]>(() => uniqueById([props.users, props.viewers, props.editors]))

// Roles sit above users in both selects; a selected role's chip reads "<name> (role)".
const roleSelectOptions = computed(() => (props.roleOptions || []).map((r) => ({ key: roleKey(r), name: r.name, chipLabel: r.name + ' (role)' })))

function freshDraft() {
  const d = props.dashboard || {}
  return {
    name: d.name || '',
    description: d.description || '',
    refresh_enabled: Boolean(d.refresh_enabled),
    refresh_interval_minutes: snapRefreshInterval(d.refresh_interval_minutes),
    accessible_by_all_users: Boolean(d.accessible_by_all_users),
    viewerKeys: [
      ...(props.allowedRoles || []).filter((r) => r.can_read).map(roleKey),
      ...uniqueById([props.viewers]).map((u) => userKey(u.id)),
    ],
    editorKeys: [
      ...(props.allowedRoles || []).filter((r) => r.can_write).map(roleKey),
      ...uniqueById([props.editors]).map((u) => userKey(u.id)),
    ],
    filterBar: JSON.parse(JSON.stringify(props.filterConfig || emptyConfig())) as FilterBarConfig,
  }
}

const draft = ref(freshDraft())
const open = ref({ name: true, filters: true, access: true, refresh: true })
const REFRESH_INTERVAL_TOGGLE_OPTIONS = REFRESH_INTERVAL_OPTIONS.map(o => ({ value: o.value, label: o.value === 60 ? '1 hour' : o.value + ' min' }))

// Description shows only while a tile is hovered; the reserved line height keeps the grid from jumping.
const describedKey = ref<FilterKey | null>(null)

function toggleKey(k: FilterKey) {
  const keys = draft.value.filterBar.keys
  draft.value.filterBar.keys = keys.includes(k) ? keys.filter(x => x !== k) : [...keys, k]
}
const errs = ref({ name: '' })

function userLabel(u: User): string {
  if (u.first_name && u.last_name) return u.first_name + ' ' + u.last_name
  if (u.first_name) return u.first_name
  if (u.last_name) return u.last_name
  return u.email || ''
}

// Mirrors the merge the editor used to do: a user in both lists gets both flags.
function mergePermissions(viewers: User[], editors: User[]): AllowedUser[] {
  const allowed: Record<number, AllowedUser> = {}
  for (const u of viewers) {
    if (!allowed[u.id]) allowed[u.id] = { id: u.id, can_read: true, can_write: false }
    else allowed[u.id].can_read = true
  }
  for (const u of editors) {
    if (!allowed[u.id]) allowed[u.id] = { id: u.id, can_read: false, can_write: true }
    else allowed[u.id].can_write = true
  }
  return Object.values(allowed)
}

// Keys are stored in registry order; the checklist can't reorder them.
const orderedKeys = computed<FilterKey[]>(() => FILTER_KEYS.filter(k => draft.value.filterBar.keys.includes(k)))

const schema = computed(() => {
  if (!props.filterOptions) return []
  return buildFilterSchema(orderedKeys.value, draft.value.filterBar.defaults, props.filterOptions)
})

function onDefaultsChange(values: Record<string, any>) {
  draft.value.filterBar.defaults = { ...draft.value.filterBar.defaults, ...values }
}

function validate(): boolean {
  errs.value.name = draft.value.name.trim() === '' ? 'Dashboard name cannot be empty' : ''
  return !errs.value.name
}

function onSave() {
  if (!validate()) return
  const defaults: Record<string, any> = {}
  for (const k of orderedKeys.value) if (draft.value.filterBar.defaults[k] !== undefined) defaults[k] = draft.value.filterBar.defaults[k]
  if (orderedKeys.value.includes('period') && draft.value.filterBar.defaults.custom_dates) defaults.custom_dates = draft.value.filterBar.defaults.custom_dates
  emit('save', {
    name: draft.value.name.trim(),
    description: draft.value.description,
    refresh_enabled: draft.value.refresh_enabled,
    refresh_interval_minutes: snapRefreshInterval(draft.value.refresh_interval_minutes),
    accessible_by_all_users: draft.value.accessible_by_all_users,
    allowed_users: mergePermissions(
      userIdsFromKeys(draft.value.viewerKeys).map((id) => ({ id })),
      userIdsFromKeys(draft.value.editorKeys).map((id) => ({ id })),
    ),
    allowed_roles: mergeRolePermissions(draft.value.viewerKeys, draft.value.editorKeys),
    filter_bar: { enabled: draft.value.filterBar.enabled, keys: orderedKeys.value, defaults },
  })
}

function onClose() {
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show) onClose()
}

watch(
  () => props.show,
  (isOpen) => {
    if (isOpen) {
      draft.value = freshDraft()
      errs.value = { name: '' }
      open.value = { name: true, filters: true, access: true, refresh: true }
      document.addEventListener('keydown', onEscKey)
    } else {
      document.removeEventListener('keydown', onEscKey)
    }
  },
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>
